// Renders activities filtered by a CRM entity. Used inside detail pages.
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";

interface Props {
  /** Which CRM entity to filter by. */
  field: "contactId" | "dealId" | "accountId" | "candidateId" | "campaignId";
  id: string;
  /** Optional activity type filter (TASK / MEETING / EMAIL / CALL / NOTE). */
  type?: "TASK" | "MEETING" | "EMAIL" | "CALL" | "NOTE" | "LINKEDIN_MESSAGE";
  /** Open = not completed; past = already done. If omitted, shows all. */
  status?: "open" | "past";
  emptyMessage?: string;
}

export function ActivitiesPanel({ field, id, type, status, emptyMessage = "Nothing here yet." }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["/activities", { field, id, type, status }],
    queryFn: async () => {
      const params: Record<string, unknown> = { [field]: id, pageSize: 50 };
      if (type) params.type = type;
      const r = await api.get("/activities", { params });
      return r.data.data as any[];
    },
  });

  let rows = data ?? [];
  if (status === "open") rows = rows.filter((a) => !a.completedAt);
  if (status === "past") rows = rows.filter((a) => !!a.completedAt);

  if (isLoading) return <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>;
  if (rows.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>;

  return (
    <ul className="space-y-2">
      {rows.map((a) => {
        const done = !!a.completedAt || a.status === "COMPLETED";
        return (
          <li key={a.id} className="rounded-md border bg-card p-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{a.type}</Badge>
                <span className={cn("font-medium", done && "line-through text-muted-foreground")}>{a.subject}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {a.scheduledAt ? new Date(a.scheduledAt).toLocaleString() : new Date(a.createdAt).toLocaleDateString()}
              </span>
            </div>
            {a.body && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.body}</p>}
            {a.outcome && <p className="mt-1 text-xs"><span className="text-muted-foreground">Outcome:</span> {a.outcome}</p>}
          </li>
        );
      })}
    </ul>
  );
}
